import authStoreGetters from '../authentication/store/getters'
import * as Sentry from '@sentry/vue'
import { gql } from '@apollo/client/core'
import {
  ArcDto,
  ArcGetArgs,
  ArcGetByIdArgs,
  ArcGetNotificationsArgs,
  ArcNotificationsDto,
  ArcPagedResultDto,
  ArcReviewDto,
  bsConstants,
  CreateArcDto,
  EligibleArcDto,
  EntityDeletionSummaryDto,
  IdArgs,
  ReportPirateArgs,
  UpdateArcDto
} from 'booksprout'
import { arcFragments } from './fragments'
import { AxiosResponse } from 'axios'
import { getArcResult } from './arcHelpers'
import { useApollo } from '../apollo/apolloModule'
import { useAxios } from '../axios/axiosModule'
import { useBaseCrud } from '../../composables/useBaseCrud'
import { arcReviewStatsService } from './arcReviewStatsService'

/**
 * The arc lifecycle: read one or a page of arcs, create, update, delete, duplicate, end early, and the
 * two side reads that belong to an arc rather than to its claims (its notifications and its reviews).
 *
 * One of three services that replace `ArcService`, split along the lanes its specs were already
 * written in (arc.service.crud.spec.ts here). `useArc().arc.service` composes all three, so consumers
 * see the surface they always saw; anything new should reach for the narrow factory it needs.
 *
 * `duplicate` is the one member that crosses lanes — it copies an arc and then has to know which
 * download codes were used — so this file imports that one query from arcReviewStatsService. The
 * dependency runs one way only.
 */

const getArcs = (params?: ArcGetArgs): Promise<ArcPagedResultDto> => {
  return useApollo().query({
    variables: {
      ...params
    },
    query: gql`
      query GetArcs (
        $needle: String,
        $activeOnly: Boolean,
        $scheduledOnly: Boolean,
        $finishedOnly: Boolean,
        $draftOnly: Boolean,
        $skip: Int,
        $take: Int,
        $sortBy: String,
        $descending: Boolean,
        $arcType: Int,
        $withNewReviewActivityStatus: Boolean,
        $forUserId: Int
      ) {
        arcs (
          needle: $needle,
          activeOnly: $activeOnly,
          scheduledOnly: $scheduledOnly,
          finishedOnly: $finishedOnly,
          draftOnly: $draftOnly,
          skip: $skip,
          take: $take,
          sortBy: $sortBy,
          descending: $descending,
          arcType: $arcType,
          withNewReviewActivityStatus: $withNewReviewActivityStatus,
          forUserId: $forUserId
        ) {
          totalRows,
          items {
            ...ArcFragment
            ...ArcDownloadCodesFragment
          }
        }
      }
      ${arcFragments.arc}
      ${arcFragments.arcDownloadCodes}
    `
  }).then((result: ArcPagedResultDto) => {
    return {
      totalRows: result.totalRows,
      items: result.items.map(getArcResult)
    }
  })
}

/**
 * `args` is everything but the id, which arrives as the first parameter and is merged in below. Third
 * service in stage 6 whose getById declared the full args type — penName and arcTeam were the others —
 * and the third one no caller could satisfy: app-reviewer's Review page passes
 * `{ skipEligibleCheck, genericSearch }`. `BaseCrudServiceInterface.getById (id?, args?: any)` is what
 * kept all three quiet.
 *
 * Without `genericSearch` this is the owner's read - the API refuses anyone else - so only then does it ask for
 * the audiobook code lists. A reader's read leaves them out of the query altogether [sc-28919].
 */
const getById = (id?: number, args?: Omit<ArcGetByIdArgs, 'id'>): Promise<ArcDto | null> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id,
      ...args,
      withDownloadCodes: !args?.genericSearch
    },
    query: gql`
      query GetArc (
        $id: Int!,
        $genericSearch: Boolean,
        $allowOwnClaim: Boolean,
        $skipEligibleCheck: Boolean,
        $withDownloadCodes: Boolean!
      ) {
        arc (id: $id, genericSearch: $genericSearch, allowOwnClaim: $allowOwnClaim, skipEligibleCheck: $skipEligibleCheck) {
          ...ArcFragment
          ...ArcDownloadCodesFragment @include(if: $withDownloadCodes)
        }
      }
      ${arcFragments.arc}
      ${arcFragments.arcDownloadCodes}
    `
  }).then((arcDto: any) => {
    return getArcResult(arcDto)
  })
}

const getEligibleById = (id?: number, args?: Omit<ArcGetByIdArgs, 'id'>): Promise<EligibleArcDto> => {
  return useApollo().query({
    variables: {
      id,
      ...args
    },
    query: gql`
      query GetEligibleArc (
        $id: Int!,
        $genericSearch: Boolean
      ) {
        eligibleArc (id: $id, genericSearch: $genericSearch) {
          isEligible,
          arc {
            ...ArcFragment
          }
        }
      }
      ${arcFragments.arc}
    `
  }).then((eligibleArcDto: any) => {
    return {
      isEligible: eligibleArcDto.isEligible,
      arc: getArcResult(eligibleArcDto.arc) as ArcDto
    }
  })
}

const createUpdateVars = (model: CreateArcDto | UpdateArcDto) => {
  return {
    specialInformation: model.specialInformation,
    arcType: model.arcType,
    discoverableType: model.discoverableType,
    participationType: model.participationType,
    participationPreventionType: model.participationPreventionType,
    siteWideParticipationPreventionType: model.siteWideParticipationPreventionType,
    copyCountType: model.copyCountType,
    copyCount: model.copyCount,
    seriesLimitType: model.seriesLimitType,
    reviewFlags: model.reviewFlags,
    optionalReviewFlags: model.optionalReviewFlags,
    requiredReviewCount: model.requiredReviewCount,
    liveDate: model.liveDate,
    reviewDays: model.reviewDays,
    stopDownloadDate: model.stopDownloadDate,
    neverEndingPrivate: model.neverEndingPrivate,
    bookId: model.bookId,
    arcTeamId: model.arcTeamId,
    usDownloadCodes: model.usDownloadCodes,
    ukDownloadCodes: model.ukDownloadCodes,
    authorsDirectDownloadCodes: model.authorsDirectDownloadCodes,
    koboDownloadCodes: model.koboDownloadCodes,
    vipExclusiveAccessDays: model.vipExclusiveAccessDays,
    dueDate: model.dueDate,
    alertFlags: model.alertFlags,
    liveDateStr: model.liveDateStr,
    stopDownloadDateStr: model.stopDownloadDateStr,
    dueDateStr: model.dueDateStr
  }
}

const create = (model: CreateArcDto): Promise<ArcDto> => {
  if (!model.bookId) {
    const error = new Error('CreateArc called without bookId')
    Sentry.withScope(scope => {
      scope.setTag('operation', 'CreateArc')
      scope.setContext('arcModel', { bookId: model.bookId, arcType: model.arcType, arcTeamId: model.arcTeamId })
      Sentry.captureException(error)
    })
    return Promise.reject(error)
  }
  return useApollo().mutate({
    variables: {
      ...createUpdateVars(model)
    },
    mutation: gql`
      mutation CreateArc (
        $specialInformation: String,
        $arcType: Int,
        $discoverableType: Int,
        $participationType: Int,
        $participationPreventionType: Int,
        $siteWideParticipationPreventionType: Int,
        $copyCountType: Int,
        $copyCount: Int,
        $seriesLimitType: Int,
        $reviewFlags: Int,
        $optionalReviewFlags: Int,
        $requiredReviewCount: Int,
        $liveDate: DateTime,
        $reviewDays: Int,
        $stopDownloadDate: DateTime,
        $neverEndingPrivate: Boolean,
        $bookId: Int!,
        $arcTeamId: Int,
        $usDownloadCodes: String,
        $ukDownloadCodes: String,
        $authorsDirectDownloadCodes: String,
        $koboDownloadCodes: String,
        $vipExclusiveAccessDays: Int,
        $dueDate: DateTime,
        $alertFlags: Int,
        $liveDateStr: String,
        $stopDownloadDateStr: String,
        $dueDateStr: String
      ) {
        createArc (
          specialInformation: $specialInformation,
          arcType: $arcType,
          discoverableType: $discoverableType,
          participationType: $participationType,
          participationPreventionType: $participationPreventionType,
          siteWideParticipationPreventionType: $siteWideParticipationPreventionType,
          copyCountType: $copyCountType,
          copyCount: $copyCount,
          seriesLimitType: $seriesLimitType,
          reviewFlags: $reviewFlags,
          optionalReviewFlags: $optionalReviewFlags,
          requiredReviewCount: $requiredReviewCount,
          liveDate: $liveDate,
          reviewDays: $reviewDays,
          stopDownloadDate: $stopDownloadDate,
          neverEndingPrivate: $neverEndingPrivate,
          bookId: $bookId,
          arcTeamId: $arcTeamId,
          usDownloadCodes: $usDownloadCodes,
          ukDownloadCodes: $ukDownloadCodes,
          authorsDirectDownloadCodes: $authorsDirectDownloadCodes,
          koboDownloadCodes: $koboDownloadCodes,
          vipExclusiveAccessDays: $vipExclusiveAccessDays,
          dueDate: $dueDate,
          alertFlags: $alertFlags,
          liveDateStr: $liveDateStr,
          stopDownloadDateStr: $stopDownloadDateStr,
          dueDateStr: $dueDateStr
        ) {
          ...ArcFragment
          ...ArcDownloadCodesFragment
        }
      }
      ${arcFragments.arc}
      ${arcFragments.arcDownloadCodes}
    `
  }).then((arc: any) => getArcResult(arc) || new ArcDto())
}

const update = (model: UpdateArcDto): Promise<ArcDto> => {
  if (!model.bookId) {
    const error = new Error('UpdateArc called without bookId')
    Sentry.withScope(scope => {
      scope.setTag('operation', 'UpdateArc')
      scope.setContext('arcModel', { id: model.id, bookId: model.bookId, arcType: model.arcType, arcTeamId: model.arcTeamId })
      Sentry.captureException(error)
    })
    return Promise.reject(error)
  }
  return useApollo().mutate({
    variables: {
      id: model.id,
      ...createUpdateVars(model)
    },
    mutation: gql`
      mutation UpdateArc (
        $id: Int!,
        $specialInformation: String,
        $arcType: Int,
        $discoverableType: Int,
        $participationType: Int,
        $participationPreventionType: Int,
        $siteWideParticipationPreventionType: Int,
        $copyCountType: Int,
        $copyCount: Int,
        $seriesLimitType: Int,
        $reviewFlags: Int,
        $optionalReviewFlags: Int,
        $requiredReviewCount: Int,
        $liveDate: DateTime,
        $reviewDays: Int,
        $stopDownloadDate: DateTime,
        $neverEndingPrivate: Boolean,
        $bookId: Int!,
        $arcTeamId: Int,
        $usDownloadCodes: String,
        $ukDownloadCodes: String,
        $authorsDirectDownloadCodes: String,
        $koboDownloadCodes: String,
        $vipExclusiveAccessDays: Int,
        $dueDate: DateTime,
        $alertFlags: Int,
        $liveDateStr: String,
        $stopDownloadDateStr: String,
        $dueDateStr: String
      ) {
        updateArc (
          id: $id,
          specialInformation: $specialInformation,
          arcType: $arcType,
          discoverableType: $discoverableType,
          participationType: $participationType,
          participationPreventionType: $participationPreventionType,
          siteWideParticipationPreventionType: $siteWideParticipationPreventionType,
          copyCountType: $copyCountType,
          copyCount: $copyCount,
          seriesLimitType: $seriesLimitType,
          reviewFlags: $reviewFlags,
          optionalReviewFlags: $optionalReviewFlags,
          requiredReviewCount: $requiredReviewCount,
          liveDate: $liveDate,
          reviewDays: $reviewDays,
          stopDownloadDate: $stopDownloadDate,
          neverEndingPrivate: $neverEndingPrivate,
          bookId: $bookId,
          arcTeamId: $arcTeamId,
          usDownloadCodes: $usDownloadCodes,
          ukDownloadCodes: $ukDownloadCodes,
          authorsDirectDownloadCodes: $authorsDirectDownloadCodes,
          koboDownloadCodes: $koboDownloadCodes,
          vipExclusiveAccessDays: $vipExclusiveAccessDays,
          dueDate: $dueDate,
          alertFlags: $alertFlags,
          liveDateStr: $liveDateStr,
          stopDownloadDateStr: $stopDownloadDateStr,
          dueDateStr: $dueDateStr
        ) {
          ...ArcFragment
          ...ArcDownloadCodesFragment
        }
      }
      ${arcFragments.arc}
      ${arcFragments.arcDownloadCodes}
    `
  }).then((arc: any) => getArcResult(arc) || new ArcDto())
}

const checkDelete = (id: number): Promise<EntityDeletionSummaryDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query CheckDeleteArc ($id: Int!) {
        checkDeleteArc (id: $id) {
          count,
          dtoTypeName,
          ids
        }
      }
    `
  })
}

const deleteArc = (id?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation ConfirmDeleteArc (
        $id: Int!
      ) {
        confirmDeleteArc(id: $id) {
          id
        }
      }
    `
  })
}

const reportFileFactory = (files: any[], args: ReportPirateArgs): object => {
  return {
    url: crud.getApiControllerUrl('report-pirate'),
    withCredentials: true,
    headers: [
      { name: 'Authorization', value: `Bearer ${authStoreGetters.authToken}` },
      { name: 'CSRF-Token', value: authStoreGetters.csrfToken }
    ],
    formFields: [
      // ReportPirateArgs shape
      { name: 'bookId', value: args.bookId },
      { name: 'fileName', value: files[0].name },
      { name: 'link', value: args.link }
    ]
  }
}

const sendBookFileUpdateNotification = (bookId: number, reason: number): Promise<number> => {
  return useApollo().mutate({
    variables: {
      bookId,
      reason
    },
    mutation: gql`
      mutation SendBookFileUpdateNotification (
        $bookId: Int!
        $reason: Int!
      ) {
        sendBookFileUpdateNotification (
          bookId: $bookId
          reason: $reason
        )
      }
    `
  })
}

const endImmediately = (args: IdArgs): Promise<ArcDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation EndImmediately (
        $id: Int!
      ) {
        endImmediately (
          id: $id
        ) {
          ...ArcFragment
        }
      },
      ${arcFragments.arc}
    `
  })
}

const getNotifications = (args: ArcGetNotificationsArgs): Promise<ArcNotificationsDto[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetArcNotifications (
        $id: Int!
        $notificationCodes: [String!]!
      ) {
        arcNotifications (
          id: $id
          notificationCodes: $notificationCodes
        ) {
          notification {
            id,
            active,
            code,
            lastQueuedDate,
            templateId,
            typeFlags,
            status,
            statusDate,
            userAwaitingNotificationCount,
            userNotificationCount
          },
          sentCount,
          totalCount
        }
      }
    `
  })
}

const getReviews = (id: number): Promise<ArcReviewDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query GetArcReviews (
        $id: Int!
      ) {
        arcReviews (id: $id) {
          id,
          claim {
            id,
            userId,
            status,
            canFollowTeam,
            arc {
              id,
              book {
                title
              }
            },
            user {
              id,
              name,
              image,
            }
          },
          createdDate,
          disclaimer,
          privateFeedback,
          title,
          review,
          reviewLanguage,
          translatedReview,
          rating,
          sites {
            id,
            link,
            site,
            status,
            statusDate,
            statusReason,
            exempt
          },
          userId
        }
      }
    `
  })
}

const getDuplicateRunningOrScheduledArcs = (args: {
  bookId: number,
  arcType: number,
  currentArcId: number
}): Promise<any[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetDuplicateRunningOrScheduledArcs (
        $bookId: Float
        $arcType: Float
        $currentArcId: Float
      ) {
        getDuplicateRunningOrScheduledArcs (
          bookId: $bookId,
          arcType: $arcType,
          currentArcId: $currentArcId
        ) {
          id,
          bookId,
          arcType,
          liveDate,
          stopDownloadDate
        }
      }
    `
  })
}

/**
 * Saved arcs (sc-26587). Both resolve to the arc's id and the caller ignores it — the reviewer's
 * list toggles optimistically and reverts on a rejection.
 */
const saveArc = async (id: number): Promise<any> => {
  await useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation SaveArc (
        $id: Int!
      ) {
        saveArc(id: $id) {
          id
        }
      }
    `
  })
}

const unsaveArc = async (id: number): Promise<any> => {
  await useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation UnsaveArc (
        $id: Int!
      ) {
        unsaveArc(id: $id) {
          id
        }
      }
    `
  })
}

const fullUrlFromShortUrl = (url: string): Promise<any> => {
  return useAxios().axios().post('arc/full-url-from-short-url', { url }).then((r: AxiosResponse) => {
    return r.data
  })
}

const duplicate = async (arc: ArcDto): Promise<ArcDto> => {
  // Initially just copy the arc object
  const newArcDto: CreateArcDto = {
    ...(arc as any),
    bookId: arc.bookId || 0
  }
  
    // For audiobooks, filter out used download codes
    if (arc.arcType === bsConstants.ARCS.TYPE.AUDIO_BOOK) {
      const usedCodes = await arcReviewStatsService().getUsedDownloadCodes(arc.id)
      if (usedCodes) {
        const filterUsedCodes = (codesStr: string): string => {
          if (!codesStr) return codesStr
          const codes = codesStr.split(',').filter(Boolean)
          const availableCodes = codes.filter(code => 
            !usedCodes.some(used => used.code === code)
          )
          return availableCodes.length > 0 ? availableCodes.join(',') : ''
        }
        
        // Filter each download code field
        newArcDto.usDownloadCodes = filterUsedCodes(arc.usDownloadCodes)
        newArcDto.ukDownloadCodes = filterUsedCodes(arc.ukDownloadCodes)
        newArcDto.authorsDirectDownloadCodes = filterUsedCodes(arc.authorsDirectDownloadCodes)
        newArcDto.koboDownloadCodes = filterUsedCodes(arc.koboDownloadCodes)
      }
    }
  
  // Remove fields that we don't want to copy (date fields), or aren't needed for the create call
  const fieldsToRemove = [
    'liveDate', 'stopDownloadDate', 'dueDate', 'dueCutOffDate',
    'liveDateStr', 'stopDownloadDateStr', 'dueDateStr', 'dueCutOffDateStr',
    'createdDate', 'updatedDate'
  ]
  
  fieldsToRemove.forEach(field => delete (newArcDto as any)[field])

  // Create and return the new arc
  return service.create(newArcDto)
}

const crud = useBaseCrud<ArcDto>('arc')

/**
 * `globalLoader` is chained by the list mixins, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get: getArcs,
  getById,
  getEligibleById,
  createUpdateVars,
  create,
  update,
  checkDelete,
  delete: deleteArc,
  endImmediately,
  sendBookFileUpdateNotification,
  getNotifications,
  getReviews,
  getDuplicateRunningOrScheduledArcs,
  fullUrlFromShortUrl,
  duplicate,
  reportFileFactory,
  saveArc,
  unsaveArc,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function arcService () {
  return service
}
